import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Avatar,
  Button,
  Typography,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Modal from "./Modal";
import MakeOfferModal from "./MakeOfferModal";
import StartChatButton from "./StartChatButton";
import FormatTime from "../AdsPage/FormatTime";
import LoadingAnimation from "../Animations/LoadingAnimation";
import {
  cardHover,
  cardOutline,
  fonts,
  headingFonts,
  mainButtonStyles,
  noOutline,
  semiBoldFonts,
} from "../../Styles/Styles";

const ListedBy = ({
  userId,
  uniqueId,
  adId,
  isSold,
  receiveOffers,
  minOfferAmount,
}) => {
  const [userData, setUserData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState();
  const [isRevealed, setIsRevealed] = useState(false);
  const loggedInUser = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const location = useLocation();

  // Check if the navigation state indicates the modal should be open
  useEffect(() => {
    // Check if modal should be opened based on navigation state or session storage
    const showModal =
      location.state?.showModal && !sessionStorage.getItem("modalClosed");

    if (showModal) {
      setShowOfferModal(true);
    }

    // Clear navigation state after the first render to avoid reopening on refresh
    window.history.replaceState({}, document.title);
  }, [location.state]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/singleuser/${userId}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }
        const data = await response.json();
        setUserData(data);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, [userId]);

  const handleShowPhoneNumber = async () => {
    if (loggedInUser) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/get-phone-number/${userId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const data = await response.json();

        if (response.ok) {
          setPhoneNumber(data.user); // Assuming API returns { phone: "+13451234567" }
          setIsRevealed(true);
        } else {
          console.error("Error fetching phone number:", data.message);
        }
      } catch (error) {
        console.error("Error fetching phone number:", error);
      }
    } else {
      setShowModal(true); // Show the modal if the user is not logged in
    }
  };

  const closeModal = () => {
    setShowModal(false); // Function to close the modal
  };
  const openOfferModal = () => {
    if (loggedInUser) {
      setShowOfferModal(true); // Function to close the modal
    } else {
      setShowModal(true);
    }
  };
  const closeOfferModal = () => {
    setShowOfferModal(false); // Function to close the modal
  };
  const viewUserProfile = () => {
    navigate(`/profile/${userData._id}`);
  };

  return (
    <Card
      sx={{
        p: 2,
        mt: 2,
        ...cardOutline,
        ...cardHover,
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ ...headingFonts }}>
          Listed by
        </Typography>
        {userData ? (
          <>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar
                src={
                  userData.image ||
                  "https://elasticbeanstalk-us-east-1-908027412287.s3.us-east-1.amazonaws.com/uploads/ProfileImages/default.png"
                }
                onClick={viewUserProfile}
                alt={userData.firstName}
                sx={{ cursor: "pointer", width: 56, height: 56, mr: 2 }}
              />
              <Box>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    onClick={viewUserProfile}
                    sx={{
                      ...headingFonts,
                      cursor: "pointer",
                    }}
                  >
                    {`${userData.firstName} ${userData.lastName}`}
                  </Typography>
                  {userData.isVerifiedbyID === "approved" && (
                    <Tooltip
                      title="This user's identity has been verified through a government ID check."
                      arrow
                    >
                      <IconButton
                        size="small"
                        sx={{
                          color: "green",
                          ...noOutline,
                        }}
                      >
                        <VerifiedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ...fonts }}
                >
                  <strong>Member since:</strong>{" "}
                  <FormatTime createdAt={userData.createdAt} />
                </Typography>
              </Box>
            </Box>
            <StartChatButton
              sellerId={userId}
              adId={adId}
              uniqueId={uniqueId}
            />
            {!isSold && receiveOffers === true && (
              <Button
                onClick={openOfferModal}
                className="main-button-style"
                sx={{
                  mt: 1,
                  ...mainButtonStyles,
                }}
              >
                <span>Make Offer</span>
                <RequestQuoteOutlinedIcon fontSize="small" />
              </Button>
            )}
            {userData.showPhoneNumber && (
              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#DDECF7",
                  padding: "10px 15px",
                  borderRadius: "50px",
                  gap: 2,
                  color: "white",
                  // width: "fit-content",
                  width: "100%",
                }}
              >
                {/* Phone Icon */}
                <IconButton
                  disabled
                  sx={{
                    backgroundColor: "white !important", // Background color for the button
                  }}
                >
                  <LocalPhoneOutlinedIcon
                    fontSize="small"
                    sx={{ color: "var(--primary-color)" }}
                  />
                </IconButton>

                {/* Phone Number Display */}
                <Typography
                  letterSpacing={1}
                  sx={{
                    ...semiBoldFonts,
                    color: "var(--primary-color)",
                  }}
                >
                  {phoneNumber
                    ? `${phoneNumber.countryCode} ${phoneNumber.phoneNumber}`
                    : "+1345*******"}
                </Typography>

                {/* Eye Icon Button */}
                <IconButton
                  onClick={handleShowPhoneNumber}
                  sx={{
                    backgroundColor: "var(--primary-color)",
                    "&:hover": {
                      backgroundColor: "var(--primary-color)", // Hover background color (optional)
                    },
                    "&:focus, &:active": {
                      outline: "none",
                    },
                  }}
                  disabled={isRevealed}
                >
                  {!isRevealed && (
                    <VisibilityOutlinedIcon sx={{ color: "white" }} />
                  )}
                </IconButton>
              </Box>
            )}
          </>
        ) : (
          <LoadingAnimation />
        )}
      </CardContent>

      <MakeOfferModal
        minOfferAmount={minOfferAmount}
        open={showOfferModal}
        onClose={closeOfferModal}
        adId={adId}
        uniqueId={uniqueId}
      />
      <Modal open={showModal} onClose={closeModal} />
    </Card>
  );
};

export default ListedBy;
